<template>
  <div class="legal-document-home">
    <!-- 标题区域 仅在1、2状态显示 -->
    <div v-if="searched === 1 || searched === 2" class="header-home">
      <h1>诉讼文书</h1>
      <p>输入需求或上传材料，生成可编辑诉讼类文书。</p>
    </div>

    <!-- 1、2状态才渲染输入/表单 -->
    <template v-if="searched === 1 || searched === 2">
      <!-- 输入框组件 互斥显示 -->
      <DocumentInput
        v-if="searched === 1"
        v-model="keyword"
        @search="handleSearch"
        @upload="handleUpload"
        @optimize="handleOptimize"
      />

      <!-- 结构化表单 互斥显示 -->
      <div v-if="searched === 2" class="form-wrap">
        <DocumentForm
          v-model="keyword"
          :template-type="currentTemplate"
          @back="searched = 1"
          @submit="handleSubmitGenerate"
        />
      </div>

      <!-- 文书模板卡片 仅1、2状态永久展示 -->
      <div class="template-section">
        <div class="template-top">
          <h2>诉讼文书模板</h2>
          <a-input-search placeholder="搜索诉讼文书模板" style="width: 280px" />
        </div>
        <DocumentTemplateList @draft="handleStartDraft" />
      </div>
    </template>

    <!-- 公共上传弹窗 全局保留 -->
    <UploadFileModal v-model:open="modalOpen" @upload-file="handleUploadFile" />

    <!-- 文书生成加载页 仅searched=3单独展示 -->
    <!-- <template v-if="searched === 3">
      <div class="loading-wrap">
        <DocumentLoadingPanel
          :keyword="keyword"
          @finish="handleLoadFinish"
          @back="searched = 1"
        />
      </div>
    </template> -->

    <!-- 文书编辑结果页 仅searched=4单独展示 -->
    <template v-if="searched === 4">
      <!-- 编辑工具条（原挂 kevinHome 顶栏，移到编辑页内部，状态与操作同页直连） -->
      <div class="editor-page">
        <DocHeader
          class="editor-toolbar"
          :title="docHeaderTitle"
          :save-text="editorLayoutRef?.lastSaveTime || ''"
          :ready="editorReady"
          @generate="handleToolbarGenerate"
          @download="handleToolbarDownload"
          @back="handleBackHome"
        />
        <DocEditorLayout
          ref="editorLayoutRef"
          v-model="content"
          :initialQuery="initialQuery"
          :initialStructured="initialStructured"
          :keyword="keyword"
          @save="handleSave"
          @quota-exceeded="handleQuotaExceeded"
          @editor-ready="editorReady = $event"
        />
      </div>
    </template>
  </div>
</template>

<script setup>
defineOptions({ name: "LegalDocumentHome" });
import { ref, nextTick, computed, watch, inject } from "vue";
import { message } from "ant-design-vue";
// 导入所有子组件
import DocumentInput from "./DocumentInput.vue";
import DocumentTemplateList from "./DocumentTemplateList.vue";
import DocumentForm from "./DocumentForm.vue";
// import DocumentLoadingPanel from "./DocumentLoadingPanel.vue";
// 复用公共上传弹窗
import UploadFileModal from "@/components/casesearch/UploadFileModal.vue";

import DocEditorLayout from "@/components/docEditor/DocEditorLayout.vue";
import DocHeader from "@/components/docEditor/DocHeader.vue";
// 全局状态
const keyword = ref("");
// 页面状态：1=通用输入框 2=结构化表单 3=加载生成 4=结果页面
const searched = ref(1);

// 编辑器内文档是否已生成/加载（DocEditorLayout 的 editorReady 镜像），
// 控制 DocHeader 保存诉状/下载 Word 的可用性；离开结果页时复位
const editorReady = ref(false);
watch(searched, (val) => {
  if (val !== 4) editorReady.value = false;
});

// kevinHome 提供的侧边栏收起方法：进入文书生成页（点"开始生成"后）自动收起，
// 路由不变只能靠页面状态触发；与路由规则同语义，只收起不展开
const collapseSider = inject("collapseSider", () => {});
watch(searched, (val) => {
  if (val === 4) collapseSider();
});

// 当前选中模板标识 complaint/reply/appeal/defense/evidence
const currentTemplate = ref("");

/* 编辑态工具条标题：与 DocumentTemplateList 的模板名称对齐，
 * 兜底「民事起诉状」（首页直接检索、历史记录恢复等未经过模板选择的入口，
 * 与迁移前 kevinHome header 写死的标题保持一致） */
const TEMPLATE_TITLES = {
  complaint: "民事起诉状",
  reply: "答辩状",
  appeal: "上诉状",
  defense: "辩护意见",
  evidence: "证据清单",
};
const docHeaderTitle = computed(
  () => TEMPLATE_TITLES[currentTemplate.value] || "民事起诉状",
);
// 生成后的文书文本
// const documentContent = ref("");
// 文件上传弹窗开关
const modalOpen = ref(false);

const content = ref("");

// 传递给 DocEditorLayout / AiAssistantPanel 的初始数据
const initialQuery = ref("");
const initialStructured = ref(null);

const handleSave = (doc) => {
  console.log("保存文档", doc);
};
/**
 * 首页输入框直接点击【开始检索】
 */
const handleSearch = (form) => {
  keyword.value = form.keyword;
  if (!keyword.value.trim()) {
    message.warning("请输入内容");
    return;
  }
  initialQuery.value = form.keyword;
  initialStructured.value = null;
  searched.value = 4;
};

/**
 * 点击模板卡片「开始起草」，切换为表单页面
 * @param type 模板唯一标识
 */
const handleStartDraft = async (type) => {
  // 保留 DocumentInput 已输入的内容作为初始值传递给 DocumentForm
  // 仅在切换到不同模板类型时才重置
  if (currentTemplate.value !== type) {
    currentTemplate.value = type;
  }
  await nextTick();
  searched.value = 2;
};

/**
 * 表单页面提交生成文书
 */
const handleSubmitGenerate = (formText) => {
  keyword.value = formText;
  // 内容为空时，退回到普通输入框
  if (!keyword.value.trim()) {
    searched.value = 1;
    message.warning("请输入内容");
    return;
  }
  // 解析结构化数据（起诉状模板时提取字段）
  if (currentTemplate.value === "complaint") {
    const lines = formText
      .split("\n")
      .map((l) => l.trim())
      .filter(Boolean);
    initialStructured.value = {
      plaintiffInfo: extractField(lines, "我是"),
      defendantInfo: extractField(lines, "现起诉被告"),
      caseBackground: extractField(lines, "本案的案件背景是："),
      claims: extractField(lines, "诉讼请求是："),
    };
    initialQuery.value = "";
    console.log(
      initialQuery.value,
      "initialQuery1",
      keyword.value,
      "initialQuery2",
      formText,
      "initialQuery",
    );
  } else {
    initialQuery.value = formText;
    initialStructured.value = null;
  }
  searched.value = 4;
};

// 从表单行中提取用户填写的内容（去掉标签前缀，支持跨行内容）
// 占位文本对应返回空字符串，不作为字段值
function extractField(lines, prefix) {
  // 已知的段落标题前缀（用于判断是否遇到下一段）
  const knownPrefixes = ["我是", "现起诉被告", "本案的案件背景是：", "诉讼请求是："];
  // 占位文本集合：与 DocumentForm 模板中的 placeholder 保持一致
  // 这些值表示"用户没填"，对应字段应返回空字符串
  const placeholders = [
    "姓名或名称",
    "请介绍案件背景",
    "请列举诉讼请求",
    // 其他模板的占位也一并兜底
    "填写我方身份名称",
    "简述原告起诉内容",
    "陈述抗辩事实理由",
    "双方核心分歧点",
    "填写原审案件编号",
    "不服原判的事实与法律问题",
    "本次上诉诉求",
    "涉嫌刑事罪名",
    "完整案件经过",
    "可从轻、减轻处罚情节",
    "证据质证异议点",
    "民事/刑事/行政案件",
    "逐条填写证据、来源、证明目的",
  ];
  // 结尾的 footer 行（如"请基于上述信息起草一份xxx。"）也作为停止边界，
  // 防止跨行收集时把 footer 也算进"诉讼请求"里
  const footerRegex = /^请基于上述信息起草一份/;

  // 辅助：判断一段内容是否是占位文本
  const isPlaceholder = (text) => {
    if (!text) return true;
    const t = text.trim();
    if (!t) return true;
    if (placeholders.includes(t)) return true;
    return false;
  };

  for (let i = 0; i < lines.length; i++) {
    if (lines[i].includes(prefix)) {
      // 去掉前缀部分
      let rest = lines[i].slice(lines[i].indexOf(prefix) + prefix.length);
      // 去掉冒号、空格等分隔符，检查是否有实际内容
      const cleaned = rest.replace(/^[：:\s，,、。.？?!！]+/, "").trim();

      // 同行内联内容：要么是真实内容、要么是占位
      if (cleaned) {
        if (isPlaceholder(cleaned)) {
          // 同行就是占位文本 → 字段为空
          return "";
        }
        return cleaned;
      }

      // 跨行内容：寻找后续行中第一个非空行（跳过空行），收集到遇到下一段标题或 footer 为止
      const contentParts = [];
      for (let j = i + 1; j < lines.length; j++) {
        const nextLine = lines[j].trim();
        if (!nextLine) continue; // 跳过空行
        // 遇到下一个段落标题 → 停止
        const isTitleLine = knownPrefixes.some((p) => nextLine.startsWith(p));
        if (isTitleLine) break;
        // 遇到 footer 行（"请基于上述信息起草一份..."）→ 停止
        if (footerRegex.test(nextLine)) break;
        // 遇到占位文本 → 跳过（不作为字段内容）
        if (isPlaceholder(nextLine)) continue;
        contentParts.push(nextLine);
      }
      return contentParts.join("\n").trim() || "";
    }
  }
  return "";
}

/**
 * 加载步骤全部完成，跳转文书结果页
 */
// const handleLoadFinish = (resultText) => {
//   documentContent.value = resultText;
//   searched.value = 4;
// };

/**
 * 智能优化按钮
 */
const handleOptimize = () => {
  console.log("执行案情智能优化");
};

/**
 * 打开上传文件弹窗
 */
const handleUpload = () => {
  modalOpen.value = true;
};

/**
 * 上传文件回调
 */
const handleUploadFile = (file) => {
  console.log("已上传案件材料文件：", file);
};

const editorLayoutRef = ref(null);

// 起诉状生成超限处理
const handleQuotaExceeded = (msg) => {
  searched.value = 1;
  message.warning(msg || "今日起诉状生成使用次数已达上限，请明天再试");
};

/**
 * 从历史记录恢复数据（供 kevinHome 的 goCase 调用）
 */
const restoreFromHistory = async (detail) => {
  if (!detail) return;
  // detail 可能是数组，取第一个
  const record = Array.isArray(detail)
    ? detail.find((d) => d.requestParams)
    : detail;
  if (!record) return;

  // 设置 sessionId
  try {
    const params = JSON.parse(record.requestParams);
    if (params.session_id) {
      window.__docSessionId = params.session_id;
    }
  } catch (e) {
    // ignore
  }

  // 加载文档 URL，并用记录标题作为文档名称
  const docUrl = record.responseResult;
  const docName = record.title || "";
  if (docUrl) {
    searched.value = 4;
    await nextTick();
    if (editorLayoutRef.value) {
      await editorLayoutRef.value.loadDocument(docUrl, docName);
    }
  }
};

/* ===== 编辑态工具条操作（原由 kevinHome 顶栏经 activeComponentRef 远程调用，现同页直连） ===== */
// 保存诉状（上传接口）
const handleToolbarGenerate = async () => {
  await editorLayoutRef.value?.uploadToApi();
};
// 下载 Word（文件名跟随当前模板标题）
const handleToolbarDownload = async () => {
  await editorLayoutRef.value?.save(`${docHeaderTitle.value}.docx`);
};
// 返回诉讼文书首页
const handleBackHome = () => {
  searched.value = 1;
};

// 供 kevinHome 的 goCase 恢复历史记录数据
defineExpose({
  restoreFromHistory,
});
</script>

<style scoped lang="less">
.legal-document-home {
  width: 100%;
  // max-width: 1780px;
  margin: 0 auto;
  margin-top: 24px;
  // padding: 0 20px;
}

/* 编辑态整页布局：工具条 + 编辑器纵向排布，总高与原来编辑器单占时一致
   （64 header + 24 上边距 + 24 下边距 = 112），避免页面出现滚动 */
.editor-page {
  height: calc(100vh - 112px);
  display: flex;
  flex-direction: column;
  gap: 12px;
}

/* 工具条卡片化（DocHeader 自身是 50px 白条，这里补圆角/描边贴合页面风格） */
.editor-toolbar {
  flex-shrink: 0;
  border-radius: 10px;
  border: 1px solid #e6ebf2;
  box-shadow: 0 6px 18px rgba(31, 45, 61, 0.05);
}

/* 编辑器填满工具条以下剩余高度（覆盖其自身的 calc 高度） */
.editor-page :deep(.doc-editor-layout) {
  flex: 1;
  min-height: 0;
  height: auto;
}

.header-home {
  // margin-top: 52px;
  h1 {
    font-size: 42px;
    font-weight: 700;
    color: #12213f;
    margin-bottom: 12px;
  }
  p {
    color: #64748f;
    font-size: 16px;
    line-height: 30px;
  }
}

.template-section {
  margin-top: 40px;
  .template-top {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 20px;
    h2 {
      font-size: 20px;
      font-weight: 600;
      color: #12213f;
    }
  }
}

.form-wrap,
.loading-wrap {
  margin-top: 40px;
}

:deep(.content-wrapper .toolbar) {
  border-bottom: none !important;
  border-bottom-color: transparent !important;
}

@media (max-width: 992px) {
  .header-home h1 {
    font-size: 34px;
  }
}
@media (max-width: 768px) {
  .legal-document-home {
    padding: 20px;
  }
  .header-home h1 {
    font-size: 28px;
  }
}
</style>
